// Restrict post-auth `?next=` redirects to same-origin paths. Blocks
// `javascript:` / `data:` schemes, protocol-relative URLs, and absolute
// URLs that point to a different origin. Returns `fallback` when the
// input is missing, malformed, or fails any of these checks.
//
// We strip ASCII whitespace and control characters before parsing
// because attackers can encode them (e.g.
// `next=java%0d%0ascript%0d%0a:alert(0)`) and the WHATWG URL parser
// then collapses the result back into `javascript:`.

const SAFE_RELATIVE_PATH = /^\/(?!\/)/;

function stripUnsafeChars(input: string): string {
  let out = "";
  for (let i = 0; i < input.length; i += 1) {
    const code = input.charCodeAt(i);
    if (code > 0x20 && code !== 0x7f) {
      out += input[i];
    }
  }
  return out;
}

export function safeNextUrl(
  next: unknown,
  fallback: string = "/dashboard"
): string {
  if (typeof next !== "string" || next.length === 0) {
    return fallback;
  }

  const stripped = stripUnsafeChars(next);
  if (stripped.length === 0) {
    return fallback;
  }

  if (SAFE_RELATIVE_PATH.test(stripped)) {
    return stripped;
  }

  if (typeof window === "undefined") {
    return fallback;
  }

  let parsed: URL;
  try {
    parsed = new URL(stripped, window.location.origin);
  } catch {
    return fallback;
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return fallback;
  }

  if (parsed.origin !== window.location.origin) {
    return fallback;
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
